/**
 * Visão por LOTE: os ambientes combinados (`combinar.js`) agrupados pelo
 * NÚMERO DO LOTE da planilha. O lote é a unidade que a fábrica corta junto;
 * ver o lote espalhado por setores mostra quem está segurando os outros.
 *
 * As PEÇAS DESMEMBRADAS entram pelo ambiente de origem: o lote mostra quantas
 * peças dele estão fora e quais são, porque uma porta sozinha noutro setor é
 * exatamente o tipo de coisa que atrasa a montagem do lote inteiro sem
 * aparecer em lugar nenhum.
 */
import { normalizeText } from "../../../utils/data";
import { compareDatesAsc } from "../../../utils/status";
import { CATALOGO_VAZIO } from "../setores";
import { STATUS_AMBIENTE } from "./vocabulario";

export const CHAVE_SEM_LOTE = "__sem_lote__";
export const ROTULO_SEM_LOTE = "Sem lote";

const LISTA_VAZIA = Object.freeze([]);

function porPrazoEfetivo(a, b) {
  return (
    compareDatesAsc(a.prazoEfetivo, b.prazoEfetivo) ||
    String(a.rotulo || "").localeCompare(String(b.rotulo || ""), "pt-BR")
  );
}

function menorPrazo(demandas) {
  return demandas.reduce((menor, d) => {
    if (!d.prazoEfetivo) return menor;
    return !menor || d.prazoEfetivo < menor ? d.prazoEfetivo : menor;
  }, null);
}

function contarPorSetor(demandas) {
  const contagem = {};
  demandas.forEach((d) => {
    if (d.setorId) contagem[d.setorId] = (contagem[d.setorId] || 0) + 1;
  });
  return contagem;
}

// Ordem das salas no mapa; setor que não está no catálogo vai para o fim.
function ordenarSetores(ids, catalogo) {
  return [...ids].sort((a, b) => catalogo.ordem(a) - catalogo.ordem(b) || String(a).localeCompare(String(b)));
}

function pecasDoLote(demandas, pecasPorAmbiente) {
  if (pecasPorAmbiente.size === 0) return LISTA_VAZIA;
  const chaves = new Set(demandas.map((d) => d.chave).filter(Boolean));
  const pecas = [];
  chaves.forEach((chave) => pecas.push(...(pecasPorAmbiente.get(chave) || [])));
  return pecas;
}

function resumirLote(chave, lote, demandas, { catalogo, pecasPorAmbiente }) {
  const ordenadas = [...demandas].sort(porPrazoEfetivo);
  const porSetor = contarPorSetor(ordenadas);
  const pecas = pecasDoLote(ordenadas, pecasPorAmbiente);
  return {
    chave,
    lote,
    semLote: chave === CHAVE_SEM_LOTE,
    rotulo: chave === CHAVE_SEM_LOTE ? ROTULO_SEM_LOTE : `Lote ${lote}`,
    demandas: ordenadas,
    setores: ordenarSetores(Object.keys(porSetor), catalogo),
    porSetor,
    prazoEfetivo: menorPrazo(ordenadas),
    atrasadas: ordenadas.filter((d) => d.atrasada).length,
    emProducao: ordenadas.filter((d) => d.statusApp === STATUS_AMBIENTE.EM_PRODUCAO).length,
    pecas,
    pecasFora: pecas.filter((peca) => peca.fora).length,
  };
}

// Lote "145" antes de "1450"; texto que não é número vai por ordem alfabética.
function porNumeroDoLote(a, b) {
  const na = Number(a.lote);
  const nb = Number(b.lote);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return String(a.lote).localeCompare(String(b.lote), "pt-BR", { numeric: true });
}

function ordenarLotes(a, b) {
  if (a.semLote !== b.semLote) return a.semLote ? 1 : -1;
  return compareDatesAsc(a.prazoEfetivo, b.prazoEfetivo) || porNumeroDoLote(a, b);
}

function agruparPecas(pecas) {
  const mapa = new Map();
  (pecas || []).forEach((peca) => {
    if (!peca?.ambienteChave) return;
    if (!mapa.has(peca.ambienteChave)) mapa.set(peca.ambienteChave, []);
    mapa.get(peca.ambienteChave).push(peca);
  });
  return mapa;
}

/**
 * [{chave, lote, semLote, rotulo, demandas, setores, porSetor, prazoEfetivo,
 *   atrasadas, emProducao, pecas, pecasFora}], do lote com prazo mais apertado
 * ao mais folgado; "Sem lote" sempre por último.
 *
 * `opcoes` = {catalogo (para a ordem das salas), pecas (combinadas,
 * `pecas.js`)}.
 */
export function agruparPorLote(demandas, { catalogo = CATALOGO_VAZIO, pecas = LISTA_VAZIA } = {}) {
  if (!Array.isArray(demandas)) return [];
  const contexto = { catalogo, pecasPorAmbiente: agruparPecas(pecas) };
  const grupos = new Map();
  demandas.forEach((demanda) => {
    const lote = String(demanda.lote ?? "").trim();
    const chave = normalizeText(lote) || CHAVE_SEM_LOTE;
    if (!grupos.has(chave)) grupos.set(chave, { lote: lote || null, demandas: [] });
    grupos.get(chave).demandas.push(demanda);
  });
  return [...grupos.entries()]
    .map(([chave, grupo]) => resumirLote(chave, grupo.lote, grupo.demandas, contexto))
    .sort(ordenarLotes);
}
